/**
 * SurfaceFootprintGizmo — visual + interactive feedback for the rectangle-
 * footprint surface tools (Surface Box and Surface Cylinder).
 *
 * The two operators share the same draft shape (`SurfaceFootprintDraftState`,
 * a basis frame plus two corners + signed height) and the same three-stage
 * modal flow (select → footprint → height). They differ only in:
 *   - the wireframe geometry rendered during the height stage (cube vs. cylinder)
 *   - the footprint border (square vs. ellipse)
 *   - the footprint fill mesh (plane vs. circle)
 *   - the per-edge hit volumes (rectangle corners vs. ellipse loop)
 *
 * This component is parameterised by `kind` and selects the right
 * geometry / vertex set per kind. Everything else (basis math, footprint
 * placement, `SurfaceExtrudeArrow` wiring, render order) is shared.
 *
 *  - **Footprint stage**: a translucent rectangle / circle on the locked
 *    face plane, plus a wireframe border. Lives in the basis frame and
 *    tracks the in-progress drag (driven by `SurfaceBoxHandler` for both
 *    Box and Cylinder).
 *  - **Height stage**: a single-axis interactive arrow gizmo along the
 *    basis normal at the footprint center, plus a live cube / cylinder
 *    wireframe so the user previews the final solid. Once the silhouette
 *    is drawn the handler releases pointer ownership and the user must
 *    grab this arrow to extrude — same pattern as `OperatorGizmo`'s
 *    extrude in Edit Mode.
 */
import { type ThreeEvent, useFrame, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useRef } from "react";
import * as THREE from "three/webgpu";
import { useStore } from "../../app/store.ts";
import type { SurfaceFootprintDraftState } from "../../app/storeTypes.ts";
import { selectSurfaceToolOfKind } from "../../app/surfaceToolSelectors.ts";
import { cubeTransformFromDraft } from "../../modeling/surfaceBoxMath.ts";
import { SURFACE_PREVIEW_HEIGHT_EPSILON } from "../../modeling/surfaceConstants.ts";
import { sampleSurfaceEllipseLoop } from "../../modeling/surfaceProfiles.ts";
import { screenToWorldRadius } from "../screenRadius.ts";
import {
  DEFAULT_OPACITY,
  GIZMO_SCREEN_PX,
  OP_HANDLE_COLOR,
  OP_HANDLE_COLOR_HOVER,
  SURFACE_CUT_PREVIEW_COLOR,
  SURFACE_CUT_PREVIEW_COLOR_HOVER,
} from "./constants.ts";
import { applyGizmoRenderOrder } from "./renderOrder.ts";
import { SurfaceExtrudeArrow } from "./SurfaceExtrudeArrow.tsx";
import {
  consumeSurfaceTargetPointer,
  SurfaceLoopEdgeTargetHits,
} from "./SurfaceProfileTargetHits.tsx";
import {
  applyLinePositions,
  buildSurfaceFootprintProfilePreview,
} from "./surfaceProfilePreview.ts";
import { useSurfaceToolGizmoCallbacks } from "./useSurfaceToolGizmoCallbacks.ts";

// Geometry is +Y-aligned — used as the "from" vector in setFromUnitVectors.
const _upY = new THREE.Vector3(0, 1, 0);

const CYLINDER_PREVIEW_SEGMENTS = 32;

// ---------------------------------------------------------------------------
// Shared static geometries — built once at module load.
// ---------------------------------------------------------------------------

/** Unit cube wireframe — 12 edges over the [-0.5, 0.5]^3 box. */
const CUBE_WIRE_GEOMETRY = (() => {
  const corners: [number, number, number][] = [];
  for (const sx of [-0.5, 0.5])
    for (const sy of [-0.5, 0.5])
      for (const sz of [-0.5, 0.5]) corners.push([sx, sy, sz]);
  // Pairs of indices forming each of the 12 edges (axis-aligned).
  const edges: [number, number][] = [
    [0, 1],
    [2, 3],
    [4, 5],
    [6, 7], // along Z
    [0, 2],
    [1, 3],
    [4, 6],
    [5, 7], // along Y
    [0, 4],
    [1, 5],
    [2, 6],
    [3, 7], // along X
  ];
  const positions = new Float32Array(edges.length * 6);
  for (let i = 0; i < edges.length; i += 1) {
    const [a, b] = edges[i];
    positions[i * 6 + 0] = corners[a][0];
    positions[i * 6 + 1] = corners[a][1];
    positions[i * 6 + 2] = corners[a][2];
    positions[i * 6 + 3] = corners[b][0];
    positions[i * 6 + 4] = corners[b][1];
    positions[i * 6 + 5] = corners[b][2];
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  return geo;
})();

/**
 * Unit-square wireframe (1×1 in XY plane), used as the box footprint
 * border. Re-scaled per frame to match the live footprint size.
 */
const SQUARE_WIRE_GEOMETRY = (() => {
  const positions = new Float32Array([
    -0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, 0.5, 0.5, 0, -0.5,
    0.5, 0, -0.5, 0.5, 0, -0.5, -0.5, 0,
  ]);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  return geo;
})();

const CIRCLE_WIRE_GEOMETRY = (() => {
  const positions = new Float32Array(CYLINDER_PREVIEW_SEGMENTS * 6);
  let o = 0;
  for (let i = 0; i < CYLINDER_PREVIEW_SEGMENTS; i += 1) {
    const j = (i + 1) % CYLINDER_PREVIEW_SEGMENTS;
    const a = (i / CYLINDER_PREVIEW_SEGMENTS) * Math.PI * 2;
    const b = (j / CYLINDER_PREVIEW_SEGMENTS) * Math.PI * 2;
    positions[o++] = Math.cos(a) * 0.5;
    positions[o++] = Math.sin(a) * 0.5;
    positions[o++] = 0;
    positions[o++] = Math.cos(b) * 0.5;
    positions[o++] = Math.sin(b) * 0.5;
    positions[o++] = 0;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  return geo;
})();

const CYLINDER_WIRE_GEOMETRY = (() => {
  const lineCount = CYLINDER_PREVIEW_SEGMENTS * 3;
  const positions = new Float32Array(lineCount * 6);
  let o = 0;
  for (let i = 0; i < CYLINDER_PREVIEW_SEGMENTS; i += 1) {
    const j = (i + 1) % CYLINDER_PREVIEW_SEGMENTS;
    const a = (i / CYLINDER_PREVIEW_SEGMENTS) * Math.PI * 2;
    const b = (j / CYLINDER_PREVIEW_SEGMENTS) * Math.PI * 2;
    const ax = Math.cos(a) * 0.5;
    const ay = Math.sin(a) * 0.5;
    const bx = Math.cos(b) * 0.5;
    const by = Math.sin(b) * 0.5;
    positions[o++] = ax;
    positions[o++] = ay;
    positions[o++] = -0.5;
    positions[o++] = bx;
    positions[o++] = by;
    positions[o++] = -0.5;
    positions[o++] = ax;
    positions[o++] = ay;
    positions[o++] = 0.5;
    positions[o++] = bx;
    positions[o++] = by;
    positions[o++] = 0.5;
    positions[o++] = ax;
    positions[o++] = ay;
    positions[o++] = -0.5;
    positions[o++] = ax;
    positions[o++] = ay;
    positions[o++] = 0.5;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  return geo;
})();

// ---------------------------------------------------------------------------
// Per-kind config — what differs between Box and Cylinder.
// ---------------------------------------------------------------------------

type FootprintKind = "box" | "cylinder";

interface FootprintGeometry {
  /** Wireframe rendered during the height stage (cube edges or cylinder edges). */
  silhouetteWire: THREE.BufferGeometry;
  /** Footprint border in the basis plane (square or circle). */
  footprintWire: THREE.BufferGeometry;
}

const FOOTPRINT_GEOMETRY: Record<FootprintKind, FootprintGeometry> = {
  box: {
    silhouetteWire: CUBE_WIRE_GEOMETRY,
    footprintWire: SQUARE_WIRE_GEOMETRY,
  },
  cylinder: {
    silhouetteWire: CYLINDER_WIRE_GEOMETRY,
    footprintWire: CIRCLE_WIRE_GEOMETRY,
  },
};

const RECTANGLE_HIT_VERTICES: [number, number, number][] = [
  [0.5, 0.5, 0],
  [-0.5, 0.5, 0],
  [-0.5, -0.5, 0],
  [0.5, -0.5, 0],
];

// Pre-allocated scratch — reused across frames to avoid GC churn in useFrame.
const _scratchPos = new THREE.Vector3();
const _scratchNormal = new THREE.Vector3();
const _scratchTangent = new THREE.Vector3();
const _scratchBitangent = new THREE.Vector3();
const _scratchBasisOrigin = new THREE.Vector3();
const _scratchMat = new THREE.Matrix4();
const _scratchQuat = new THREE.Quaternion();
const _scratchScale = new THREE.Vector3();

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface SurfaceFootprintGizmoProps {
  kind: FootprintKind;
}

export function SurfaceFootprintGizmo({ kind }: SurfaceFootprintGizmoProps) {
  const tool = useStore((s) => selectSurfaceToolOfKind(s, kind));
  const stage = tool?.stage ?? null;
  const draft: SurfaceFootprintDraftState | null = tool?.draft ?? null;
  const { camera, size: viewportSize } = useThree();

  const groupRef = useRef<THREE.Group>(null);
  const arrowGroupRef = useRef<THREE.Group>(null);
  const footprintMeshRef = useRef<THREE.Mesh>(null);
  const footprintWireRef = useRef<THREE.LineSegments>(null);
  const silhouetteWireRef = useRef<THREE.LineSegments>(null);
  const edgeHitGroupRef = useRef<THREE.Group>(null);
  const silhouetteWireGeometryRef = useRef(new THREE.BufferGeometry());

  // Edge-hit vertices: rectangle corners for box, sampled ellipse for
  // cylinder. The cylinder set depends on segment count only, so memoise
  // once.
  const edgeHitVertices = useMemo<[number, number, number][]>(() => {
    if (kind === "box") return RECTANGLE_HIT_VERTICES;
    return sampleSurfaceEllipseLoop(0.5, 0.5, CYLINDER_PREVIEW_SEGMENTS).map(
      ([x, y]) => [x, y, 0] as [number, number, number],
    );
  }, [kind]);

  const visible =
    (stage === "footprint" || stage === "height") && draft !== null;
  const previewColor =
    draft && draft.height < 0 ? SURFACE_CUT_PREVIEW_COLOR : OP_HANDLE_COLOR;
  const previewHoverColor =
    draft && draft.height < 0
      ? SURFACE_CUT_PREVIEW_COLOR_HOVER
      : OP_HANDLE_COLOR_HOVER;

  // Re-engage the silhouette-stage active flag for the duration of the
  // gizmo drag so `OrbitControls` stays disabled — same shape as
  // `OperatorGizmo`'s `commandBuffer` gating. The callback hook owns
  // setHeight / setDragging / confirm / setProfileTarget — all four are
  // pure narrowed wrappers around setSurfaceTool.
  const callbacks = useSurfaceToolGizmoCallbacks(kind);
  const { setHeight, setDragging, confirm } = callbacks;

  // Captured at pointerdown by `SurfaceExtrudeArrow`. Read live from the
  // store so the projection axis matches whatever silhouette is on screen.
  const computeAnchor = useCallback((): [number, number, number] | null => {
    const d = selectSurfaceToolOfKind(useStore.getState(), kind)?.draft ?? null;
    if (!d) return null;
    return buildSurfaceFootprintProfilePreview(d, kind).anchor;
  }, [kind]);
  const computeNormal = useCallback((): [number, number, number] | null => {
    const d = selectSurfaceToolOfKind(useStore.getState(), kind)?.draft ?? null;
    return d ? d.basis.normal : null;
  }, [kind]);

  const selectFace = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      if (stage !== "height") return;
      consumeSurfaceTargetPointer(e);
      callbacks.setProfileFace();
    },
    [stage, callbacks],
  );

  const selectEdge = useCallback(
    (edgeIndex: number, e: ThreeEvent<PointerEvent>) => {
      if (stage !== "height") return;
      consumeSurfaceTargetPointer(e);
      callbacks.setProfileEdge(edgeIndex);
    },
    [stage, callbacks],
  );

  // Per-frame: orient the gizmo group to track the basis + current draft.
  useFrame(() => {
    if (!visible || !draft || !groupRef.current) return;
    const { basis } = draft;
    _scratchTangent.set(basis.tangent[0], basis.tangent[1], basis.tangent[2]);
    _scratchBitangent.set(
      basis.bitangent[0],
      basis.bitangent[1],
      basis.bitangent[2],
    );
    _scratchNormal.set(basis.normal[0], basis.normal[1], basis.normal[2]);
    _scratchMat.makeBasis(_scratchTangent, _scratchBitangent, _scratchNormal);

    const t = cubeTransformFromDraft(draft);
    const profilePreview = buildSurfaceFootprintProfilePreview(draft, kind);
    _scratchPos.set(
      t.position[0] - (draft.height / 2) * basis.normal[0],
      t.position[1] - (draft.height / 2) * basis.normal[1],
      t.position[2] - (draft.height / 2) * basis.normal[2],
    );
    _scratchMat.decompose(_scratchBasisOrigin, _scratchQuat, _scratchScale);

    if (footprintMeshRef.current) {
      const f = footprintMeshRef.current;
      f.position.copy(_scratchPos);
      f.quaternion.copy(_scratchQuat);
      f.scale.set(Math.abs(t.width), Math.abs(t.depth), 1);
      f.visible = stage === "footprint" || stage === "height";
    }
    if (footprintWireRef.current) {
      const w = footprintWireRef.current;
      w.position.copy(_scratchPos);
      w.quaternion.copy(_scratchQuat);
      w.scale.set(Math.abs(t.width), Math.abs(t.depth), 1);
      w.visible = stage === "footprint" || stage === "height";
    }
    if (edgeHitGroupRef.current) {
      const g = edgeHitGroupRef.current;
      g.position.copy(_scratchPos);
      g.quaternion.copy(_scratchQuat);
      g.scale.set(Math.abs(t.width), Math.abs(t.depth), 1);
      g.visible = stage === "height";
    }

    if (silhouetteWireRef.current) {
      applyLinePositions(
        silhouetteWireGeometryRef.current,
        profilePreview.wirePositions,
      );
      silhouetteWireRef.current.geometry = silhouetteWireGeometryRef.current;
      silhouetteWireRef.current.position.set(0, 0, 0);
      silhouetteWireRef.current.rotation.set(0, 0, 0);
      silhouetteWireRef.current.scale.set(1, 1, 1);
      silhouetteWireRef.current.visible =
        stage === "height" &&
        Math.abs(draft.height) > SURFACE_PREVIEW_HEIGHT_EPSILON;
    }

    if (arrowGroupRef.current) {
      const a = arrowGroupRef.current;
      _scratchPos.set(
        profilePreview.anchor[0],
        profilePreview.anchor[1],
        profilePreview.anchor[2],
      );
      a.position.copy(_scratchPos);
      a.quaternion.setFromUnitVectors(_upY, _scratchNormal);
      a.scale.setScalar(
        screenToWorldRadius(
          GIZMO_SCREEN_PX,
          _scratchPos,
          camera,
          viewportSize.height,
        ),
      );
      a.visible = stage === "height";
    }
  });

  // Stamp render order so the gizmo overlays scene geometry consistently —
  // mirrors the OperatorGizmo's layering. Empty deps array: groupRef is
  // stable across renders so this only needs to run once on mount.
  useEffect(() => {
    const g = groupRef.current;
    if (!g) return;
    applyGizmoRenderOrder(g, 999);
  }, []);

  if (!visible) return null;

  const geometry = FOOTPRINT_GEOMETRY[kind];

  return (
    <group ref={groupRef} userData={{ excludeFromExport: true }}>
      {/* Translucent footprint fill — circle for cylinder, plane for box. */}
      <mesh ref={footprintMeshRef} renderOrder={998} onPointerDown={selectFace}>
        {kind === "cylinder" ? (
          <circleGeometry args={[0.5, 64]} />
        ) : (
          <planeGeometry args={[1, 1]} />
        )}
        <meshBasicMaterial
          color={previewColor}
          transparent
          opacity={0.15}
          depthTest={false}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Footprint wireframe border. */}
      <lineSegments
        ref={footprintWireRef}
        geometry={geometry.footprintWire}
        renderOrder={999}
      >
        <lineBasicMaterial
          color={previewColor}
          transparent
          opacity={DEFAULT_OPACITY}
          depthTest={false}
          depthWrite={false}
        />
      </lineSegments>

      {/* Live silhouette wireframe (height stage). */}
      <lineSegments
        ref={silhouetteWireRef}
        geometry={geometry.silhouetteWire}
        renderOrder={999}
      >
        <lineBasicMaterial
          color={previewColor}
          transparent
          opacity={DEFAULT_OPACITY}
          depthTest={false}
          depthWrite={false}
        />
      </lineSegments>

      <group ref={edgeHitGroupRef}>
        <SurfaceLoopEdgeTargetHits
          vertices={edgeHitVertices}
          onSelectFace={selectFace}
          onSelectEdge={selectEdge}
        />
      </group>

      {/* Height-axis arrow — interactive: grabbing it begins the extrude
          drag and releasing confirms (same pattern as OperatorGizmo). */}
      <SurfaceExtrudeArrow
        ref={arrowGroupRef}
        computeAnchor={computeAnchor}
        computeNormal={computeNormal}
        setHeight={setHeight}
        confirm={confirm}
        setDragging={setDragging}
        color={previewColor}
        hoverColor={previewHoverColor}
      />
    </group>
  );
}
